import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, Prohibit, PencilSimple, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", mc_number: "", reason: "", severity: "medium" };

export default function Brokers() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const invalidate = useInvalidate();
  const { data: brokers = [], isLoading } = useList("brokers", "/brokers");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (b) => { setForm({ name: b.name, mc_number: b.mc_number, reason: b.reason, severity: b.severity }); setEditing(b.id); setOpen(true); };
  const save = async () => {
    if (!form.name) { toast.error("Broker name required"); return; }
    setSaving(true);
    try { if (editing) await api.put(`/brokers/${editing}`, form); else await api.post("/brokers", form); toast.success("Saved"); setOpen(false); invalidate("brokers"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Remove from DNU list?")) return; await api.delete(`/brokers/${id}`); invalidate("brokers"); };

  return (
    <div>
      <PageHeader title="Brokers DNU List" subtitle="Do-Not-Use brokers — avoid booking with these"
        actions={isAdmin && <Button data-testid="new-broker-btn" onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />Add Broker</Button>} />

      <Panel className="mb-4 p-4 border-red-500/30 bg-red-500/5 flex items-center gap-3">
        <Warning size={22} weight="duotone" className="text-red-400 shrink-0" />
        <p className="text-sm text-slate-600">Brokers on this list are flagged as restricted. Verify before booking any load.</p>
      </Panel>

      <Panel>
        {isLoading ? <div className="p-6 text-slate-500">Loading…</div> : brokers.length === 0 ? (
          <EmptyState icon={Prohibit} title="No restricted brokers" hint="Add brokers you want your team to avoid." />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="border-slate-200 hover:bg-transparent">
                <TableHead>Broker</TableHead><TableHead className="hidden md:table-cell">MC #</TableHead>
                <TableHead>Reason</TableHead><TableHead>Severity</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {brokers.map((b) => (
                  <TableRow key={b.id} className="border-slate-200 hover:bg-slate-50" data-testid={`broker-row-${b.id}`}>
                    <TableCell className="text-slate-900 font-medium">{b.name}</TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-sm text-slate-500">{b.mc_number || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500 max-w-xs">{b.reason || "—"}</TableCell>
                    <TableCell><StatusBadge status={b.severity} /></TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEdit(b)} className="p-1.5 text-slate-500 hover:text-blue-600"><PencilSimple size={16} /></button>
                          <button onClick={() => remove(b.id)} className="p-1.5 text-slate-500 hover:text-red-400"><Trash size={16} /></button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Broker" : "Add Restricted Broker"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Broker Name *"><Input data-testid="broker-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-slate-300" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="MC Number"><Input value={form.mc_number} onChange={(e) => setForm({ ...form, mc_number: e.target.value })} className="bg-white border-slate-300 font-mono" /></Field>
              <Field label="Severity">
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Reason for Restriction"><Textarea data-testid="broker-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="bg-white border-slate-300" rows={3} placeholder="Non-payment, fraud, slow pay…" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Cancel</Button>
            <Button data-testid="save-broker-btn" onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
